import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  RadioGroup,
  Radio,
  Button,
  Alert,
  Snackbar,
  InputLabel,
  styled,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Container,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO, isValid, startOfYear, endOfYear, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { fetchAllActiveUsers, type UserBasicInfo } from '../services/supabase/user.service';
import { calculateLeaveDays, type DayPart } from '../utils/leaveDays';
import UndertimeForm from '../components/undertime/UndertimeForm';

type ApplicationTab = 'leave' | 'undertime';

const FormPaperStyled = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  marginTop: theme.spacing(2),
  // On phones: flatten (no shadow, hairline border, tighter padding) so the form
  // doesn't sit as a heavy card and can't push past the viewport edge.
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
}));

interface LeaveApplicationData {
  id: string;
  employee_id: string;
  employee_name: string;
  leave_type: string;
  custom_reason?: string | null;
  reason: string;
  request_date: string;
  leave_start_date: string;
  leave_end_date: string;
  leave_start_part?: DayPart | null;
  leave_end_part?: DayPart | null;
  leave_days?: number | null;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
  user_role?: string;
  department?: string;
}

const LeaveApplication = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<ApplicationTab>('leave');
  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState<UserBasicInfo[]>([]);

  const isAdmin = userProfile?.role === 'admin';
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const [employeeName, setEmployeeName] = useState(userProfile ? userProfile.username : '');
  const [leaveType, setLeaveType] = useState<string>('vacation');
  const [otherReason, setOtherReason] = useState('');
  const [requestDate] = useState(new Date());
  const [leaveStartDate, setLeaveStartDate] = useState<Date | null>(new Date());
  const [leaveEndDate, setLeaveEndDate] = useState<Date | null>(new Date());
  const [leaveStartPart, setLeaveStartPart] = useState<DayPart>('whole');
  const [leaveEndPart, setLeaveEndPart] = useState<DayPart>('whole');
  const [leaveDays, setLeaveDays] = useState<string>('1');
  const [leaveDaysManuallySet, setLeaveDaysManuallySet] = useState(false);
  const [leaveReason, setLeaveReason] = useState('');

  const showCustomReason = leaveType === 'other';

  const recomputeLeaveDays = useCallback(() => {
    if (!leaveStartDate || !leaveEndDate) return;
    const computed = calculateLeaveDays({
      startDate: leaveStartDate,
      endDate: leaveEndDate,
      startPart: leaveStartPart,
      endPart: leaveEndPart,
      excludeSundays: true,
    });
    setLeaveDays(String(computed));
  }, [leaveStartDate, leaveEndDate, leaveStartPart, leaveEndPart]);

  useEffect(() => {
    if (!leaveStartDate || !leaveEndDate) return;
    if (leaveDaysManuallySet) return;
    recomputeLeaveDays();
  }, [leaveStartDate, leaveEndDate, leaveStartPart, leaveEndPart, leaveDaysManuallySet, recomputeLeaveDays]);

  useEffect(() => {
    if (!userProfile) return;
    setEmployeeName(userProfile.username);
    if (!isAdmin) {
      setSelectedUserId(userProfile.id);
    }
  }, [userProfile, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const users = await fetchAllActiveUsers();
        setAllUsers(users);
        if (userProfile) {
          setSelectedUserId(userProfile.id);
        }
      } catch (err: any) {
        console.error('Error fetching users:', err);
        setErrorMessage(err.message || 'Failed to load users list');
        setErrorOpen(true);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [isAdmin, userProfile]);

  const handleUserChange = (event: SelectChangeEvent) => {
    const userId = event.target.value;
    setSelectedUserId(userId);
    const selectedUser = allUsers.find(user => user.id === userId);
    if (selectedUser) {
      setEmployeeName(selectedUser.username);
    } else if (userId === userProfile?.id && userProfile) {
      setEmployeeName(userProfile.username);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    setErrorOpen(false);
    setSuccessOpen(false);

    if (!leaveStartDate || !leaveEndDate) {
      setErrorMessage('Start and end dates are required.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }
    if (leaveEndDate < leaveStartDate) {
      setErrorMessage('End date cannot be before start date.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }
    if (!userProfile) {
      setErrorMessage('User profile not found. Please log in again.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }

    const effectiveUserId = isAdmin && selectedUserId ? selectedUserId : userProfile.id;
    const currentEmployeeName =
      (isAdmin && selectedUserId && allUsers.find(u => u.id === selectedUserId)?.username) ||
      userProfile.username;

    const isAdminSubmittingForOthers = isAdmin && selectedUserId && selectedUserId !== userProfile.id;

    const parsedLeaveDays = Number(leaveDays);
    if (!Number.isFinite(parsedLeaveDays) || parsedLeaveDays <= 0) {
      setErrorMessage('Number of leave days must be greater than 0.');
      setErrorOpen(true);
      setLoading(false);
      return;
    }

    const leaveData = {
      employee_id: effectiveUserId,
      employee_name: currentEmployeeName,
      leave_type: leaveType.toLowerCase(),
      custom_reason: leaveType === 'other' ? otherReason : null,
      reason: leaveReason + (isAdminSubmittingForOthers ? ` (Submitted by admin: ${userProfile.username})` : ''),
      request_date: format(new Date(), 'yyyy-MM-dd'),
      leave_start_date: format(leaveStartDate, 'yyyy-MM-dd'),
      leave_end_date: format(leaveEndDate, 'yyyy-MM-dd'),
      leave_start_part: leaveStartPart,
      leave_end_part: leaveEndPart,
      leave_days: Math.round(parsedLeaveDays * 2) / 2,
      status: 'pending' as const,
    };

    try {
      const { error } = await supabase.from('leave_applications').insert(leaveData).select();
      if (error) throw error;

      setSuccessOpen(true);
      setLeaveType('vacation');
      setOtherReason('');
      setLeaveReason('');
      setLeaveStartDate(new Date());
      setLeaveEndDate(new Date());
      setLeaveStartPart('whole');
      setLeaveEndPart('whole');
      setLeaveDays('1');
      setLeaveDaysManuallySet(false);

      if (isAdmin && userProfile) {
        setSelectedUserId(userProfile.id);
        setEmployeeName(userProfile.username);
      } else if (userProfile) {
        setEmployeeName(userProfile.username);
      }
    } catch (error: any) {
      console.error('Error submitting leave application:', error);
      setErrorMessage(error.message || 'Failed to submit leave application.');
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const LeaveApplicationHistory = () => {
    const [userApplications, setUserApplications] = useState<LeaveApplicationData[]>([]);
    const [loadingApplications, setLoadingApplications] = useState<boolean>(true);
    const [filterLeaveType, setFilterLeaveType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');

    const fetchUserApplications = useCallback(async () => {
      if (!userProfile) return;
      setLoadingApplications(true);
      try {
        const { data, error } = await supabase
          .from('leave_applications_view')
          .select('*')
          .eq('employee_id', userProfile.id)
          .order('submitted_at', { ascending: false });

        if (error) throw error;
        setUserApplications((data || []) as LeaveApplicationData[]);
      } catch (err: any) {
        console.error('Error fetching applications:', err);
      } finally {
        setLoadingApplications(false);
      }
    }, [userProfile]);

    useEffect(() => {
      fetchUserApplications();
    }, [fetchUserApplications, successOpen]);

    const filteredApplications = userApplications.filter((app) => {
      if (filterLeaveType !== 'all' && (app.leave_type || '').toLowerCase() !== filterLeaveType) {
        return false;
      }
      if (filterStatus !== 'all' && app.status !== filterStatus) {
        return false;
      }
      return true;
    });

    const approvedThisYearSummary = React.useMemo(() => {
      const now = new Date();
      const yearStart = startOfDay(startOfYear(now));
      const yearEnd = startOfDay(endOfYear(now));

      const breakdown: Record<string, number> = {};
      let total = 0;

      userApplications
        .filter((a) => a.status === 'approved')
        .forEach((a) => {
          const rawStart = parseISO(a.leave_start_date);
          const rawEnd = parseISO(a.leave_end_date);
          if (!isValid(rawStart) || !isValid(rawEnd)) return;

          if (rawEnd < yearStart || rawStart > yearEnd) return;

          const clippedStart = rawStart < yearStart ? yearStart : rawStart;
          const clippedEnd = rawEnd > yearEnd ? yearEnd : rawEnd;

          const startPart: DayPart =
            rawStart < yearStart ? 'whole' : ((a.leave_start_part as DayPart) || 'whole');
          const endPart: DayPart =
            rawEnd > yearEnd ? 'whole' : ((a.leave_end_part as DayPart) || 'whole');

          const days =
            typeof a.leave_days === 'number' && rawStart >= yearStart && rawEnd <= yearEnd
              ? a.leave_days
              : calculateLeaveDays({
                  startDate: clippedStart,
                  endDate: clippedEnd,
                  startPart,
                  endPart,
                  excludeSundays: true,
                });

          const typeKey = (a.leave_type || 'other').toString().toLowerCase();
          const rounded = Math.round(days * 2) / 2;
          if (!Number.isFinite(rounded) || rounded <= 0) return;

          total += rounded;
          breakdown[typeKey] = (breakdown[typeKey] || 0) + rounded;
        });

      const items = Object.entries(breakdown)
        .map(([type, days]) => ({ type, days: Math.round(days * 2) / 2 }))
        .sort((a, b) => b.days - a.days);

      return { total: Math.round(total * 2) / 2, items };
    }, [userApplications]);

    return (
      <Box sx={{ mt: { xs: 3, sm: 4 } }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom sx={{ fontWeight: 600 }}>
          Leave Application History
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Approved leave (this year)
          </Typography>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{ fontWeight: 800, lineHeight: 1.1, mt: 0.25 }}
          >
            {approvedThisYearSummary.total} days
          </Typography>
          <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {approvedThisYearSummary.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No approved leaves yet for this year.
              </Typography>
            ) : (
              approvedThisYearSummary.items.map((it) => (
                <Chip
                  key={it.type}
                  variant="outlined"
                  color="success"
                  size="small"
                  label={`${it.type.replace(/\b\w/g, (c) => c.toUpperCase())}: ${it.days}d`}
                />
              ))
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Sundays are excluded. Totals may be adjusted for holidays by admin overrides.
          </Typography>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Leave Type</InputLabel>
              <Select
                value={filterLeaveType}
                label="Leave Type"
                onChange={(e) => setFilterLeaveType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="vacation">Vacation</MenuItem>
                <MenuItem value="sick">Sick</MenuItem>
                <MenuItem value="bereavement">Bereavement</MenuItem>
                <MenuItem value="maternity/paternity">Maternity/Paternity</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loadingApplications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              boxShadow: 3,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table
              size={isMobile ? 'small' : 'medium'}
              sx={{ minWidth: isMobile ? 900 : 650 }}
              aria-label="leave applications table"
            >
              <TableHead sx={{ backgroundColor: 'grey.200' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Leave Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Dates</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Submitted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApplications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No applications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApplications.map((app) => (
                    <TableRow key={app.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{app.employee_name}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{app.leave_type}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {format(parseISO(app.leave_start_date), 'MM/dd/yyyy')} -{' '}
                        {format(parseISO(app.leave_end_date), 'MM/dd/yyyy')}
                      </TableCell>
                      <TableCell sx={{ minWidth: isMobile ? 260 : undefined }}>{app.reason}</TableCell>
                      <TableCell>
                        <Chip
                          label={app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          color={
                            app.status === 'approved'
                              ? 'success'
                              : app.status === 'rejected'
                                ? 'error'
                                : app.status === 'pending'
                                  ? 'warning'
                                  : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {format(parseISO(app.submitted_at), 'MM/dd/yyyy')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          gutterBottom
          sx={{ fontWeight: 700, mb: { xs: 1.5, sm: 2 } }}
        >
          {activeTab === 'leave' ? 'Leave Application Form' : 'Undertime Application Form'}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_, value: ApplicationTab) => setActiveTab(value)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="leave" label="Leave" />
          <Tab value="undertime" label="Undertime" />
        </Tabs>

        {activeTab === 'undertime' && <UndertimeForm />}

        {activeTab === 'leave' && (
        <>
        <FormPaperStyled>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={isMobile ? 2 : 3}>
              {isAdmin && (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="user-select-label">Apply Leave For</InputLabel>
                    <Select
                      labelId="user-select-label"
                      value={selectedUserId}
                      label="Apply Leave For"
                      onChange={handleUserChange}
                      disabled={loadingUsers}
                    >
                      {loadingUsers ? (
                        <MenuItem disabled>
                          <em>Loading users...</em>
                        </MenuItem>
                      ) : allUsers.length === 0 ? (
                        <MenuItem disabled>
                          <em>No users found</em>
                        </MenuItem>
                      ) : (
                        allUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.username} ({user.role})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {isAdmin && selectedUserId && selectedUserId !== userProfile?.id && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      You are applying for leave on behalf of: {employeeName}.
                    </Alert>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Employee Name"
                  value={employeeName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  InputProps={{
                    readOnly: !isAdmin || (selectedUserId ? selectedUserId === userProfile?.id : true),
                  }}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset" margin="normal">
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Reason for Requested Leave
                  </Typography>
                  <RadioGroup
                    row={!isMobile}
                    value={leaveType}
                    onChange={(e) => setLeaveType(e.target.value)}
                    sx={
                      isMobile
                        ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }
                        : undefined
                    }
                  >
                    <FormControlLabel value="vacation" control={<Radio />} label="Vacation" />
                    <FormControlLabel value="sick" control={<Radio />} label="Sick" />
                    <FormControlLabel value="bereavement" control={<Radio />} label="Bereavement" />
                    <FormControlLabel value="maternity/paternity" control={<Radio />} label="Maternity/Paternity" />
                    <FormControlLabel value="other" control={<Radio />} label="Other" />
                  </RadioGroup>
                </FormControl>
                {showCustomReason && (
                  <TextField
                    fullWidth
                    label="Specify Other Reason"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    required={leaveType === 'other'}
                    margin="normal"
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Reason / Details"
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <DatePicker label="Date Requested" value={requestDate} readOnly sx={{ width: '100%' }} />
              </Grid>

              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Leave From"
                  value={leaveStartDate}
                  onChange={(newValue) => setLeaveStartDate(newValue)}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      helperText: 'Past dates are allowed for emergency or late-filed leave.',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Leave To"
                  value={leaveEndDate}
                  onChange={(newValue) => setLeaveEndDate(newValue)}
                  sx={{ width: '100%' }}
                  minDate={leaveStartDate || undefined}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" margin="normal">
                  <InputLabel id="leave-from-part-label">Leave From (day)</InputLabel>
                  <Select
                    labelId="leave-from-part-label"
                    value={leaveStartPart}
                    label="Leave From (day)"
                    onChange={(e) => setLeaveStartPart(e.target.value as DayPart)}
                  >
                    <MenuItem value="whole">Whole day</MenuItem>
                    <MenuItem value="half">Half day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" margin="normal">
                  <InputLabel id="leave-to-part-label">Leave To (day)</InputLabel>
                  <Select
                    labelId="leave-to-part-label"
                    value={leaveEndPart}
                    label="Leave To (day)"
                    onChange={(e) => setLeaveEndPart(e.target.value as DayPart)}
                  >
                    <MenuItem value="whole">Whole day</MenuItem>
                    <MenuItem value="half">Half day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Number of leave days"
                  value={leaveDays}
                  onChange={(e) => {
                    setLeaveDays(e.target.value);
                    setLeaveDaysManuallySet(true);
                  }}
                  onBlur={() => {
                    const n = Number(leaveDays);
                    if (Number.isFinite(n)) setLeaveDays(String(Math.max(0, Math.round(n * 2) / 2)));
                  }}
                  helperText="Auto-computed (excludes Sundays). You can edit this to account for holidays."
                  margin="normal"
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={8}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  mt: { xs: 0, sm: 2 },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    setLeaveDaysManuallySet(false);
                    recomputeLeaveDays();
                  }}
                  disabled={!leaveStartDate || !leaveEndDate}
                >
                  Recalculate days
                </Button>
              </Grid>

              <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  size="large"
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Application'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormPaperStyled>

        <Snackbar
          open={successOpen}
          autoHideDuration={6000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
            Leave application submitted successfully!
          </Alert>
        </Snackbar>
        <Snackbar
          open={errorOpen}
          autoHideDuration={6000}
          onClose={() => setErrorOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setErrorOpen(false)} severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Snackbar>

        <LeaveApplicationHistory />
        </>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default LeaveApplication;
